import React, { Component } from "react";
import ReactHtmlParser from "react-html-parser";
import { AxiosResponse } from "axios";
import { CommentResource } from "../../../../stores/CommentStore/Types";
import { CurrentUser } from "../../../../stores/CurrentUserStore/Types";
import {
  getRequest,
  postRequest,
  deleteRequest,
} from "../../../../utils/httpRequest";
import { errorNotification } from "../../../../utils/helpers";
import CommentReplies from "./CommentReplies";

interface Props {
  currentUser: CurrentUser;
  comment: CommentResource;
  commentableId: number;
  commentType: string;
  enrolmentId: number;
  questionId: number;
  canReply: boolean;
  updateComments: () => void;
}

interface State {
  liking: boolean;
  liked: boolean;
  likesCount: number;
  deleting: boolean;
  viewReplies: boolean;
  comments: CommentResource[];
  commenting: boolean;
  content: string;
  showReplyCommentForm: boolean;
}

class Comment extends Component<Props, State> {
  static defaultProps = { canReply: false };

  constructor(props) {
    super(props);

    this.state = {
      liking: false,
      liked: false,
      likesCount: 0,
      deleting: false,
      content: "",
      viewReplies: false,
      comments: [],
      commenting: false,
      showReplyCommentForm: false,
    };
  }

  componentDidMount() {
    const { currentUser, comment } = this.props;

    this.setState({
      liked: comment.liker_ids.includes(currentUser.id),
      likesCount: comment.likes_count,
      comments: comment.comments,
    });
  }

  countryFlagImage = (countryCode) => {
    let img;
    try {
      img = require(
        `../../../../../../assets/images/flags/svg/${countryCode?.toLowerCase()}.svg`,
      );
    } catch {
      img = "";
    }

    return img;
  };

  likeComment = (e) => {
    e.preventDefault();
    this.setState({ liking: true });

    const { comment } = this.props;

    return postRequest(`/api/likes.json?comment_id=${comment.id}`, {})
      .then((response: AxiosResponse) => {
        this.setState({
          liking: false,
          liked: true,
          likesCount: this.state.likesCount + 1,
        });
      })
      .catch((error) => {
        this.setState({ liking: false });

        errorNotification(
          "Error liking comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  unlikeComment = (e) => {
    e.preventDefault();

    const { comment } = this.props;

    return postRequest(`/api/likes/unlike.json?comment_id=${comment.id}`, {})
      .then((response: AxiosResponse) => {
        this.setState({
          liking: false,
          liked: false,
          likesCount: this.state.likesCount - 1,
        });
      })
      .catch((error) => {
        this.setState({ liking: false });

        errorNotification(
          "Error unliking comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  deleteComment = (e) => {
    e.preventDefault();
    this.setState({ deleting: true });

    const { comment, updateComments, commentType, commentableId } = this.props;

    const url =
      commentType === "answer"
        ? `/api/comments/${comment.id}.json?answer_id=${commentableId}`
        : `/api/comments/${comment.id}.json?comment_id=${commentableId}`;

    return deleteRequest(url)
      .then((response: AxiosResponse) => {
        this.setState({ deleting: false });
        updateComments();
      })
      .catch((error) => {
        this.setState({ deleting: false });

        errorNotification(
          "Error deleting comment",
          JSON.stringify(error.response.data),
        );
      });
  };

  handleContentChange = (e) => {
    this.setState({ content: e.target.value });
  };

  fetchComments = () => {
    const { comment } = this.props;

    return getRequest(`/api/comments.json?comment_id=${comment.id}`)
      .then((response: AxiosResponse) => {
        this.setState({
          comments: response.data,
          viewReplies: response.data.length > 0,
        });
      })
      .catch((error) => {
        errorNotification(
          "Error loading comments",
          JSON.stringify(error.response.data),
        );
      });
  };

  submitComment = (e) => {
    const { comment, enrolmentId, questionId } = this.props;
    const { content, comments } = this.state;

    e.preventDefault();

    if (!content.trim()) {
      return;
    }

    this.setState({ commenting: true });

    return postRequest(`/api/comments.json?comment_id=${comment.id}`, {
      content,
      enrolment_id: enrolmentId,
      question_id: questionId,
    })
      .then((response: AxiosResponse) => {
        comments.unshift(response.data);
        this.setState({
          comments,
          content: "",
          commenting: false,
          viewReplies: true,
          showReplyCommentForm: false,
        });
      })
      .catch((error) => {
        this.setState({ commenting: false });

        errorNotification(
          "Error commenting",
          JSON.stringify(error.response.data),
        );
      });
  };

  handleShowReplyCommentForm = (e) => {
    e.preventDefault();
    this.setState({ showReplyCommentForm: !this.state.showReplyCommentForm });
  };

  handleViewReplies = (e) => {
    e.preventDefault();
    this.setState({ viewReplies: !this.state.viewReplies });
  };

  initials = () => {
    const { user } = this.props.comment;
    const name =
      user?.short_name_split || user?.short_name || user?.protected_name;

    return (
      name
        ?.split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "42"
    );
  };

  currentUserInitials = () => {
    const { currentUser } = this.props;
    const name =
      currentUser?.short_name_split ||
      currentUser?.short_name ||
      currentUser?.name;

    return (
      name
        ?.split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "42"
    );
  };

  render() {
    const {
      comment: { id, user, formatted_content, created_at, posted_on },
      currentUser,
      canReply,
    } = this.props;

    const {
      liking,
      liked,
      likesCount,
      deleting,
      content,
      comments,
      commenting,
      showReplyCommentForm,
      viewReplies,
    } = this.state;

    return (
      <div className="comment conversation" id={`comment_${id}`}>
        {deleting && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}
        <span className="av-c">
          {user?.profile_picture_url || user?.profile_picture ? (
            <img
              src={user.profile_picture_url || user.profile_picture}
              alt=""
            />
          ) : (
            this.initials()
          )}
        </span>
        {/* The full date rides on the whole comment as a tooltip, so a rest of
            the mouse anywhere on it answers "when was this?" */}
        <div className="comment__body" title={posted_on}>
          <div className="comment__top">
            <span className="comment__name">{user?.protected_name}</span>
            {user?.country && (
              <img
                className="flag"
                src={this.countryFlagImage(user.country)}
                title={user.country_name}
                alt={user.country_name || user.country}
              />
            )}
            {created_at ? (
              <span className="comment__time">{created_at}</span>
            ) : (
              <span className="comment__time comment__time--on-hover">
                {posted_on}
              </span>
            )}
          </div>
          {/* @ts-ignore */}
          <div className="comment__text">
            {ReactHtmlParser(formatted_content)}
          </div>
          <div className="comment__actions">
            <button
              className={`${liking ? "loading" : ""} ${liked ? "liked" : ""}`}
              type="button"
              aria-pressed={liked}
              onClick={liked ? this.unlikeComment : this.likeComment}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M12 21C7 16.5 3 13 3 8.6 3 5.9 5.2 4 7.5 4 9.5 4 11 5.3 12 7 13 5.3 14.5 4 16.5 4 18.8 4 21 5.9 21 8.6 21 13 17 16.5 12 21Z" />
              </svg>
              {likesCount}
            </button>
            {canReply && (
              <button type="button" onClick={this.handleShowReplyCommentForm}>
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M9 7 4 12l5 5M4 12h11a5 5 0 0 1 5 5v1" />
                </svg>
                Reply
              </button>
            )}
            {canReply && comments?.length > 0 && (
              <button
                className="replies-link"
                type="button"
                aria-expanded={viewReplies}
                onClick={this.handleViewReplies}
              >
                {viewReplies ? "Hide replies" : "View replies"}
              </button>
            )}
            {(user?.id === currentUser?.id || currentUser?.admin) && (
              <button type="button" onClick={this.deleteComment}>
                Delete
              </button>
            )}
          </div>
          {canReply && viewReplies && (
            <div className="replies is-open">
              {comments.map((comment) => (
                <CommentReplies
                  key={comment.id}
                  comment={comment}
                  currentUser={currentUser}
                  updateComments={this.fetchComments}
                />
              ))}
            </div>
          )}
          {canReply && showReplyCommentForm && (
            <form className="reply__body" onSubmit={this.submitComment}>
              <span className="av-c">
                {currentUser?.profile_picture ||
                currentUser?.profile_picture_url ? (
                  <img
                    src={
                      currentUser.profile_picture ||
                      currentUser.profile_picture_url
                    }
                    alt=""
                  />
                ) : (
                  this.currentUserInitials()
                )}
              </span>
              <div className="reply__body--content">
                <div className="who">{currentUser?.short_name} (you)</div>
                <textarea
                  value={content}
                  rows={3}
                  placeholder="Write your comment"
                  onChange={this.handleContentChange}
                  disabled={commenting}
                />
                <div className="action">
                  <button
                    className={`btn btn-blue btn--compact ${
                      commenting ? "loading" : ""
                    }`}
                    type="submit"
                    disabled={commenting || !content.trim()}
                  >
                    Post
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }
}

export default Comment;
